import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { btns } from '../../components/buttons';
import { UserText } from '../../components/UserText';
import { useAccessToken } from '../../auth/useAuth';
import { meQueryOptions } from '../../lib/meQuery';
import { firmDisplayName } from '../../intake/IntakeBrandScope';
import { ONBOARDING_PATH } from './OnboardingClientGate';
import { currentOnboardingInvitation } from './isOnboardingClient';

const cls = {
  panel:   'bg-surface border border-line border-l-[3px] border-l-bronze rounded-token p-5 shadow-panel mb-10',
  heading: 'font-serif text-[15px] font-normal m-0 mb-2 text-navy',
  body:    'text-[13px] text-body font-sans m-0 max-w-2xl',
  actions: 'mt-4 flex flex-wrap items-center gap-3',
  link:    `${btns.btnPrim} no-underline inline-block`,
};

/**
 * The Overview's link to a firm's onboarding form and requests (steward-app#2465), for a person
 * who already has a workspace and was then invited to onboarding or sent requests. The emails link
 * to `/onboarding*`; this is the way in from inside the app.
 *
 * Reads only `/me` (shared cache entry, no request of its own and no step-up on Overview load).
 * Shows for the most recent invitation while its `formClosed` is exactly `false`. A closed form
 * (the firm set up Steward for the person) and an absent flag (an older façade) both show nothing,
 * since this card would only lead to a closed notice. `/onboarding` itself sends the person on to
 * the completed or requests view, as it does from the emails.
 *
 * A routing label, not authority: the form's own reads and writes carry the checks.
 */
export function OnboardingInvitationCard() {
  const token = useAccessToken();
  const { data: me } = useQuery(meQueryOptions(token));
  const invitation = currentOnboardingInvitation(me);
  if (!invitation || invitation.formClosed !== false) return null;
  const firm = firmDisplayName(invitation.brandKey, invitation.firmName);

  return (
    <section className={cls.panel} aria-label="Your form and requests" data-testid="onboarding-invitation-card">
      <h2 className={cls.heading}>
        Your form and requests from {firm ? <UserText>{firm}</UserText> : 'your advisor'}
      </h2>
      <p className={cls.body}>
        {firm ? <UserText>{firm}</UserText> : 'Your advisor'} has requested some details from you. You can see them here.
      </p>
      <div className={cls.actions}>
        <Link to={ONBOARDING_PATH} className={cls.link}>Open</Link>
      </div>
    </section>
  );
}
